import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDownCircle, ArrowUpCircle, History, Plus, Trash2, X } from "lucide-react";

const fmt = (n, d = 2) =>
  Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });

/** Bir stok kaleminin hareketlerini gösterir + yeni giriş/çıkış ekler. */
export default function StockMovementsModal({ stock, contacts, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState("in");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stocks/${stock.id}/movements`);
      setRows(data || []);
    } catch (e) {
      toast.error("Hareketler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [stock.id]);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    let inQty = 0, outQty = 0;
    rows.forEach((r) => { if (r.type === "in") inQty += r.quantity; else outQty += r.quantity; });
    return { inQty, outQty };
  }, [rows]);

  const remove = async (mid) => {
    if (!window.confirm("Bu hareket silinsin mi? (stok bakiyesi geri yüklenir)")) return;
    try {
      await api.delete(`/stock-movements/${mid}`);
      toast.success("Silindi");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Silinemedi: " + (e?.response?.data?.detail || ""));
    }
  };

  const openForm = (t) => { setFormType(t); setShowForm(true); };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="stock-movements-modal"
           className="bg-white border-2 border-black w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <History size={16} /> Stok Hareketleri
            </h2>
            <div className="text-xs text-neutral-500 mt-0.5">
              <span className="font-mono-num">{stock.sku}</span> · {stock.name}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-xl leading-none" data-testid="mv-close">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Özet kutuları */}
          <div className="grid grid-cols-3 gap-3">
            <SummaryBox label="Mevcut" value={`${fmt(stock.qty_on_hand, 0)} ${stock.unit}`} tone="black" tid="mv-summary-onhand" />
            <SummaryBox label="Toplam Giriş" value={fmt(totals.inQty, 0)} tone="emerald" tid="mv-summary-in" />
            <SummaryBox label="Toplam Çıkış" value={fmt(totals.outQty, 0)} tone="rose" tid="mv-summary-out" />
          </div>

          {/* Aksiyon butonları */}
          <div className="flex items-center gap-2">
            <button data-testid="mv-add-in-btn" onClick={() => openForm("in")}
                    className="brutal-btn bg-emerald-600 text-white px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5">
              <ArrowDownCircle size={14} /> Giriş Ekle
            </button>
            <button data-testid="mv-add-out-btn" onClick={() => openForm("out")}
                    className="brutal-btn bg-rose-600 text-white px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5">
              <ArrowUpCircle size={14} /> Çıkış Ekle
            </button>
          </div>

          {/* Form (inline) */}
          {showForm && (
            <MovementForm
              stock={stock}
              contacts={contacts}
              type={formType}
              onCancel={() => setShowForm(false)}
              onSaved={async () => {
                setShowForm(false);
                await load();
                onChanged?.();
              }}
            />
          )}

          {/* Tarihçe tablosu */}
          <div className="brutal-card overflow-hidden">
            <table className="w-full text-sm" data-testid="mv-table">
              <thead>
                <tr className="border-b-2 border-black/80 bg-neutral-50">
                  <th className="text-left px-3 py-2 label-caps">Tarih</th>
                  <th className="text-left px-3 py-2 label-caps">Tip</th>
                  <th className="text-right px-3 py-2 label-caps">Adet</th>
                  <th className="text-right px-3 py-2 label-caps">Birim Mal.</th>
                  <th className="text-left px-3 py-2 label-caps">Cari</th>
                  <th className="text-left px-3 py-2 label-caps">Not</th>
                  <th className="text-right px-3 py-2 label-caps">Kalan</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-8 text-neutral-500 text-sm">Yükleniyor…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-neutral-500 text-sm">Henüz hareket yok.</td></tr>
                ) : rows.map((r) => {
                  const c = contacts.find((x) => x.id === r.contact_id);
                  return (
                    <tr key={r.id} className="border-b border-black/5 hover:bg-neutral-50" data-testid={`mv-row-${r.id}`}>
                      <td className="px-3 py-2 font-mono-num text-xs">{r.date}</td>
                      <td className="px-3 py-2">
                        {r.type === "in" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                            <ArrowDownCircle size={12} /> GİRİŞ
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-semibold">
                            <ArrowUpCircle size={12} /> ÇIKIŞ
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num">{fmt(r.quantity, 0)}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-neutral-700">
                        {r.unit_cost != null ? `${fmt(r.unit_cost, 4)} ₺` : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">{c?.name || "—"}</td>
                      <td className="px-3 py-2 text-xs text-neutral-600 truncate max-w-[240px]" title={r.note || ""}>
                        {r.note || "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num text-neutral-700">
                        {r.balance_after != null ? fmt(r.balance_after, 0) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => remove(r.id)} data-testid={`mv-delete-${r.id}`}
                                className="p-1.5 hover:bg-rose-50" title="Hareketi sil">
                          <Trash2 size={12} className="text-rose-600" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.7rem;outline:none;font-size:0.85rem;background:white}
                    .input:focus{border-color:#000;box-shadow:0 0 0 2px rgba(0,0,0,0.1)}`}</style>
      </div>
    </div>
  );
}

function SummaryBox({ label, value, tone, tid }) {
  const toneCls = {
    emerald: "border-emerald-600 text-emerald-700",
    rose: "border-rose-600 text-rose-700",
    black: "border-black text-black",
  }[tone] || "border-black text-black";
  return (
    <div className={`border-2 ${toneCls} p-3`} data-testid={tid}>
      <div className="label-caps text-[10px]">{label}</div>
      <div className="font-mono-num text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}

/** Yeni hareket ekleme formu. Cari zorunlu; giriş=tedarikçi/both, çıkış=müşteri/both. */
function MovementForm({ stock, contacts, type, onCancel, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [contactId, setContactId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Cari filtresi: giriş → supplier/both; çıkış → customer/both
  const eligibleContacts = useMemo(() => {
    if (type === "in") return contacts.filter((c) => c.type === "supplier" || c.type === "both");
    return contacts.filter((c) => c.type === "customer" || c.type === "both");
  }, [contacts, type]);

  const submit = async () => {
    const q = parseFloat(quantity);
    if (!(q > 0)) { toast.error("Adet sıfırdan büyük olmalı"); return; }
    if (!contactId) {
      toast.error(type === "in" ? "Tedarikçi cari seçin" : "Müşteri cari seçin");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        stock_id: stock.id, type, quantity: q, date,
        contact_id: contactId,
        note: note.trim() || null,
      };
      if (unitCost !== "") payload.unit_cost = parseFloat(unitCost);
      await api.post("/stock-movements", payload);
      toast.success(type === "in" ? "Giriş kaydedildi" : "Çıkış kaydedildi");
      onSaved();
    } catch (e) {
      toast.error("Kaydedilemedi: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setSaving(false);
    }
  };

  const isIn = type === "in";
  return (
    <div className={`border-2 ${isIn ? "border-emerald-600" : "border-rose-600"} p-4 space-y-3`} data-testid="mv-form">
      <div className="flex items-center justify-between">
        <div className="label-caps flex items-center gap-1.5">
          {isIn ? <ArrowDownCircle size={12} className="text-emerald-700" /> : <ArrowUpCircle size={12} className="text-rose-700" />}
          {isIn ? "Yeni Giriş" : "Yeni Çıkış"}
        </div>
        <button onClick={onCancel} className="p-1" data-testid="mv-form-cancel"><X size={14} /></button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="block">
          <div className="label-caps mb-1 text-[10px]">Tarih</div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 data-testid="mv-date" className="input font-mono-num" />
        </label>
        <label className="block">
          <div className="label-caps mb-1 text-[10px]">Adet *</div>
          <input type="number" step="1" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)}
                 data-testid="mv-qty" className="input font-mono-num" placeholder="0" />
        </label>
        <label className="block">
          <div className="label-caps mb-1 text-[10px]">
            Birim Maliyet {isIn && <span className="normal-case tracking-normal text-neutral-500">(boş = otomatik)</span>}
          </div>
          <input type="number" step="0.0001" min="0" value={unitCost} onChange={(e) => setUnitCost(e.target.value)}
                 data-testid="mv-unit-cost" className="input font-mono-num"
                 placeholder={isIn ? "Fiyat listesinden" : "opsiyonel"} />
        </label>
        <label className="block">
          <div className="label-caps mb-1 text-[10px]">
            {isIn ? "Tedarikçi *" : "Müşteri *"}
          </div>
          <select value={contactId} onChange={(e) => setContactId(e.target.value)}
                  data-testid="mv-contact" className="input">
            <option value="">— seçin —</option>
            {eligibleContacts.length === 0 ? (
              <option disabled>Uygun cari yok (Cariler sayfasından ekleyin)</option>
            ) : eligibleContacts.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <div className="label-caps mb-1 text-[10px]">Not</div>
        <input value={note} onChange={(e) => setNote(e.target.value)}
               data-testid="mv-note" className="input" placeholder="Opsiyonel açıklama" />
      </label>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="border border-black/30 px-4 py-2 text-xs uppercase tracking-widest hover:bg-white"
                data-testid="mv-cancel">İptal</button>
        <button onClick={submit} disabled={saving} data-testid="mv-save"
                className={`brutal-btn ${isIn ? "bg-emerald-600" : "bg-rose-600"} text-white px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-50 flex items-center gap-1.5`}>
          <Plus size={12} /> {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </div>
  );
}
